import { useContext, useEffect, useState } from 'react';
import {
  ALL_SEARCH_CATEGORIES,
  SearchCategory,
  SearchResult,
  SearchResultType,
  WP_HouseTerm,
  WP_PaginationResult,
} from '../../../model/models';
import { DirectoryFilterObject, WP_Tag } from '../../../model/tags/tags';
import { useLogger } from '../../../services/LogService';
import { MockService } from '../../../services/MockService';
import { AppContext } from '../../../state/AppContextProvider';
import { useSearchUsers } from './SearchUsersHook';
import { useSearchPosts } from './SearchPostsHook';
import { useSearchServices } from './SearchServicesHook';

const buildFakeItem = (category: SearchCategory, state) => {
  const mock = MockService.Instance;
  switch (category) {
    case 'news':
      return mock.getNewsList()[0];
    case 'events':
    case 'house':
    case 'services':
      return mock.getEventsList()[0];
    case 'users':
      return mock.getUsersList()[0];
  }
};

type SearchCategoryFunc<T> = (
  keywords: string,
  category: SearchCategory,
  max: number,
  tmpResults: SearchResult<T>[]
) => Promise<SearchResult<T>[]>;

export type PaginationFetch = (
  per_page: number,
  args: {
    keywords: string;
    category: SearchCategory;
  }
) => Promise<WP_PaginationResult<SearchResultType>>;

export interface SearchHookReturn {
  reset: () => void;
  isReset: boolean;
  loading: boolean;
  hasMore: boolean;
  search: PaginationFetch;
  page: number;
  totalPage: number;
  items: SearchResultType[];
  filter?: DirectoryFilterObject;
  changeFilter?: (tagsList: {
    [tagType: string]: WP_Tag | WP_HouseTerm | string | null;
  }) => void;
  changeFilterByUrl?: (
    house: WP_HouseTerm | null,
    otherTags: string | null
  ) => void;
  resetFilter?: () => void;
  applyFilter?: () => void;
}

/**
 * Hook useSearch()
 * @returns
 */
export const useSearch = (
  maxCat = 10,
  defaultCateg: SearchCategory = 'all'
) => {
  // use to trigger a search after reset
  const [mustSearch, setMustSearch] = useState<boolean>(false);

  // isSearching indicates a pending search (data fetching)
  const [isSearching, setIsSearching] = useState<boolean>(false);

  // isSearch indicates a search has been initiated
  const [isSearch, setIsSearch] = useState<boolean>(false);

  const [keywords, setKeywords] = useState<string>('');

  const [category, setCategory] = useState<SearchCategory>(defaultCateg);

  const logger = useLogger('useSearch');

  const searchHooks = {
    news: useSearchPosts(),
    events: useSearchPosts(),
    house: useSearchPosts(),
    services: useSearchServices(),
    users: useSearchUsers(),
    places: useSearchPosts(),
  };

  const { state } = useContext(AppContext);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fakeSearch: SearchCategoryFunc<SearchResultType> = (
    keywords,
    category,
    max,
    tmpResults
  ) => {
    return new Promise<SearchResult<SearchResultType>[]>((resolve, reject) => {
      setTimeout(() => {
        const fakeResults = Array.from({ length: max }, () => {
          return buildFakeItem(category, state);
        }).map((item) => ({
          category: category,
          item: item,
        }));
        resolve(fakeResults as SearchResult<SearchResultType>[]);
      }, Math.floor(Math.random() * 2000));
    });
  };

  const searchCategory = (keywords, category, max) => {
    logger.info(`searchCategory() ${category}, ${keywords}`);
    return searchHooks[category]
      .search(max, { keywords, category })
      .then((results) => {
        results.items.map((item) => ({
          category: category,
          item: item,
        }));
      })
      .catch((e) => logger.info('searchCategory() catched promise', e));
    // return fakeSearch(keywords, category);
  };

  const reset = () => {
    Object.values(searchHooks).map((hook: any) => hook.reset());
    setIsSearching(false);
    setIsSearch(false);
    setKeywords('');
    if (searchHooks.users.resetFilter) {
      searchHooks.users.resetFilter();
    }
  };

  const search = () => {
    Object.values(searchHooks).map((hook: any) => hook.reset());
    logger.info(`search() '${keywords}'`);
    setIsSearch(true);
    setMustSearch(true);
  };

  useEffect(() => {
    if (mustSearch === true) {
      logger.info(`useEffect() searching '${keywords}'`);
      setMustSearch(false);
      setIsSearching(true);

      const categories =
        category === 'all' ? ALL_SEARCH_CATEGORIES : [category];

      const promise = Promise.all(
        categories.map((cat) => searchCategory(keywords, cat, maxCat))
      );

      // when all done, set isSearching to false
      promise.finally(() => setIsSearching(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mustSearch]);

  const more = (category: SearchCategory) => {
    setIsSearching(true);

    const categories = category === 'all' ? ALL_SEARCH_CATEGORIES : [category];

    logger.info(
      `more() fetching ${maxCat} more results for category ${category}`
    );

    return new Promise<void>((resolve, reject) => {
      const promiseAll = Promise.all(
        categories.map((cat) => searchCategory(keywords, cat, maxCat))
      );

      promiseAll.finally(() => {
        setIsSearching(false);
        resolve();
      });
    });
  };

  const hasMore = (category: SearchCategory) => {
    return searchHooks[category].hasMore;
  };

  const getResults: (category: SearchCategory) => SearchHookReturn = (
    category
  ) => {
    return searchHooks[category];
  };

  const resultsCount = ALL_SEARCH_CATEGORIES.map(
    (c) => getResults(c).items.length
  ).reduce((a, b) => a + b, 0);

  const clear = () => {
    ALL_SEARCH_CATEGORIES.map((c) => getResults(c).reset());
  };

  const isCleared = () =>
    ALL_SEARCH_CATEGORIES.every((c) => getResults(c).isReset);

  return {
    search: search,
    keywords: keywords,
    setKeywords: setKeywords,
    getResults: getResults,
    resultsCount: resultsCount,
    isSearching: isSearching,
    isSearch: isSearch,
    category: category,
    setCategory: setCategory,
    more: more,
    hasMore: hasMore,
    reset: reset,
    clear: clear,
    isCleared: isCleared,
  };
};
